import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { Observable, of } from 'rxjs';
import { flatMap, take } from 'rxjs/operators';

import { isLocalUsingMeridianNotation } from '../../utils/meridian.helper';
import { ActionButtonIcons } from '../action-button/action-button-icons';
import { DeleteModalComponent } from '../modal/delete-modal/delete-modal.component';
import { ModalService } from '../modal/modal.service';
import { ShiftSettingModalComponent } from '../modal/shift-setting/shift-setting-modal.component';
import { HourMinute } from './models/hour-minute';
import { Shift } from './models/shift';
import * as fromStore from './store';

@Component({
  selector: 'ls-shift-settings',
  templateUrl: './shift-settings.component.html',
  styleUrls: ['./shift-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShiftSettingsComponent implements OnInit {
  public loading$: Observable<boolean>;
  public shifts$: Observable<Array<Shift>>;
  public actionButtonIcons: typeof ActionButtonIcons;
  public hourFormat: string = isLocalUsingMeridianNotation()
    ? 'hh:mm A'
    : 'HH:mm';

  constructor(
    private store: Store<fromStore.ShiftState>,
    private modal: ModalService
  ) {}

  public ngOnInit(): void {
    this.actionButtonIcons = ActionButtonIcons;
    this.store.dispatch(new fromStore.GetShiftSettings());

    this.shifts$ = this.store.select(fromStore.getShiftSettings);
    this.loading$ = this.store.select(fromStore.getShiftSettingsLoading);
  }

  public getTimeFormatted(time: HourMinute): string {
    if (time) {
      return moment()
        .startOf('day')
        .hour(time.hour)
        .minute(time.minute)
        .format(this.hourFormat);
    }
  }

  public deleteShift(event: MouseEvent, shift: Shift): void {
    event.stopPropagation();
    event.preventDefault();

    this.modal
      .open(DeleteModalComponent, {
        title: 'Delete shift',
        whatToDelete: shift.name,
        typeToDelete: 'shift'
      })
      .pipe(
        flatMap((confirm: boolean | string) => {
          if (typeof confirm !== 'string' && confirm) {
            return this.shifts$.pipe(take(1));
          }
          return of(null);
        })
      )
      .subscribe((shifts: Array<Shift>) => {
        if (shifts) {
          this.store.dispatch(
            new fromStore.DeleteShift({ shiftToDelete: shift, shifts: shifts })
          );
        }
      });
  }

  public editShift(event: MouseEvent, shiftToEdit: Shift): void {
    event.stopPropagation();
    event.preventDefault();

    this.modal
      .open(ShiftSettingModalComponent, {
        shifts: this.shifts$,
        shift: shiftToEdit
      })
      .subscribe((payload: any) => {
        if (typeof payload !== 'string' && payload) {
          this.store.dispatch(
            new fromStore.EditShift({
              shiftToEdit: payload.shift,
              shifts: payload.shifts
            })
          );
        }
      });
  }

  public openNewShiftModal(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.modal
      .open(ShiftSettingModalComponent, { shifts: this.shifts$, shift: {} })
      .subscribe((payload: any) => {
        if (typeof payload !== 'string' && payload) {
          this.store.dispatch(
            new fromStore.AddShift({
              shiftToAdd: payload.shift,
              shifts: payload.shifts
            })
          );
        }
      });
  }
}
